import type { CmsPage, CmsPageInput } from '@erp/shared';
import { ArrowLeft, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import {
  Button, confirmDialog, DataTable, type DataTableColumn, EmptyState, FormField, Input, RichTextEditor,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

import { TranslationEditor } from './TranslationEditor.js';

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

type Mode = { kind: 'list' } | { kind: 'new' } | { kind: 'edit'; page: CmsPage };

export function CmsPagesPage(): JSX.Element {
  const { t } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');
  const [pages, setPages] = useState<CmsPage[] | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'list' });

  const load = (): void => {
    void api().get<{ items: CmsPage[] }>('/cms/pages').then((r) => setPages(r.data.items)).catch(() => setPages([]));
  };
  useEffect(load, []);

  const columns: DataTableColumn<CmsPage>[] = useMemo(() => [
    {
      id: 'title', header: t('pages.title'), sortValue: (r) => r.title, searchValue: (r) => `${r.title} ${r.slug}`,
      cell: (r) => (
        <button type="button" className="text-start text-brand hover:underline" onClick={() => setMode({ kind: 'edit', page: r })}>
          {r.title}{r.isHome ? <span className="ms-2 text-xs text-fg-muted">({t('pages.home')})</span> : null}
        </button>
      ),
    },
    { id: 'slug', header: t('pages.slug'), sortValue: (r) => r.slug, cell: (r) => <span className="font-mono text-xs">/{r.slug}</span> },
    {
      id: 'status', header: t('pages.status'), sortValue: (r) => r.status,
      cell: (r) => <Badge variant={r.status === 'published' ? 'success' : 'secondary'}>{t(`pages.statusVal.${r.status}`)}</Badge>,
    },
    {
      id: 'actions', header: '', align: 'end',
      cell: (r) => canWrite ? (
        <button
          type="button"
          className="text-xs text-danger hover:underline"
          onClick={async () => {
            if (!(await confirmDialog({ message: t('pages.confirmDelete', { title: r.title }) }))) return;
            try { await api().delete(`/cms/pages/${r.id}`); load(); toast.success(t('pages.deleted')); }
            catch { toast.error(tc('errors:server')); }
          }}
        >
          {tc('actions.delete')}
        </button>
      ) : null,
    },
  ], [t, tc, canWrite]);

  if (mode.kind !== 'list') {
    return <PageForm mode={mode} onDone={() => { setMode({ kind: 'list' }); load(); }} onCancel={() => setMode({ kind: 'list' })} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">{t('pages.heading')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('pages.subtitle')}</p>
        </div>
        {canWrite && <Button onClick={() => setMode({ kind: 'new' })}><Plus className="me-1.5 h-4 w-4" aria-hidden />{t('pages.new')}</Button>}
      </header>
      {!pages ? (
        <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
      ) : pages.length === 0 ? (
        <EmptyState title={t('pages.empty')} description={t('pages.emptyHint')} />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={pages} rowKey={(r) => r.id} searchPlaceholder={t('pages.search')} />
        </Card>
      )}
    </div>
  );
}

function PageForm({ mode, onDone, onCancel }: { mode: Mode; onDone: () => void; onCancel: () => void }): JSX.Element {
  const { t } = useTranslation('cms');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('cms.write');
  const editing = mode.kind === 'edit' ? mode.page : null;
  const [slug, setSlug] = useState(editing?.slug ?? '');
  const [title, setTitle] = useState(editing?.title ?? '');
  const [body, setBody] = useState(editing?.bodyHtml ?? '');
  const [status, setStatus] = useState<'draft' | 'published'>(editing?.status ?? 'draft');
  const [isHome, setIsHome] = useState(editing?.isHome ?? false);
  const [seoTitle, setSeoTitle] = useState(editing?.seoTitle ?? '');
  const [seoDescription, setSeoDescription] = useState(editing?.seoDescription ?? '');
  const [saving, setSaving] = useState(false);

  const save = async (): Promise<void> => {
    if (!title.trim() || !SLUG.test(slug)) { toast.error(t('pages.invalid')); return; }
    setSaving(true);
    const payload: CmsPageInput = {
      slug, title: title.trim(), bodyHtml: body || null, status, isHome,
      seoTitle: seoTitle.trim() || null, seoDescription: seoDescription.trim() || null,
    };
    try {
      if (editing) await api().put(`/cms/pages/${editing.id}`, payload);
      else await api().post('/cms/pages', payload);
      toast.success(t('pages.saved'));
      onDone();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'conflict' ? t('pages.slugTaken') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <button type="button" onClick={onCancel} className="inline-flex w-fit items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden />{t('pages.back')}
      </button>
      <h1 className="text-2xl font-semibold">{editing ? t('pages.editTitle') : t('pages.new')}</h1>
      <Card className="flex flex-col gap-4 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="page-title" label={t('pages.title')} required>
            <Input id="page-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </FormField>
          <FormField id="page-slug" label={t('pages.slug')} required helperText={t('pages.slugHint')}>
            <Input id="page-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} maxLength={160} placeholder="about-us" />
          </FormField>
        </div>
        <FormField id="page-body" label={t('pages.body')}>
          <RichTextEditor id="page-body" value={body} onChange={setBody} ariaLabel={t('pages.body')} />
        </FormField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="page-seo-title" label={t('pages.seoTitle')}>
            <Input id="page-seo-title" value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} maxLength={200} />
          </FormField>
          <FormField id="page-seo-desc" label={t('pages.seoDescription')}>
            <Input id="page-seo-desc" value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} maxLength={320} />
          </FormField>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-fg-muted">{t('pages.status')}:</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'published')} className="h-8 rounded-md border border-border bg-bg px-2 text-sm">
              <option value="draft">{t('pages.statusVal.draft')}</option>
              <option value="published">{t('pages.statusVal.published')}</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isHome} onChange={(e) => setIsHome(e.target.checked)} className="h-4 w-4" />
            <span>{t('pages.isHome')}</span>
          </label>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => void save()} loading={saving}>{tc('actions.save')}</Button>
          <Button variant="ghost" onClick={onCancel}>{tc('actions.cancel')}</Button>
        </div>
      </Card>

      {editing && (
        <Card className="flex flex-col gap-3 p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('pages.translate')}</h2>
          <TranslationEditor
            entityType="cms_page"
            entityId={editing.id}
            disabled={!canWrite}
            fields={[
              { key: 'title', label: t('pages.title') },
              { key: 'body_html', label: t('pages.body'), type: 'richtext' },
              { key: 'seo_title', label: t('pages.seoTitle') },
              { key: 'seo_description', label: t('pages.seoDescription'), type: 'textarea' },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
